import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import Reveal from "./Reveal";

const MODULES = [
    { num: "00", title: "Bienvenida", sub: "Onboarding", desc: "Bienvenido a la familia.", img: "/modulo-00.webp" },
    { num: "01", title: "Fundamentos", sub: "Mentalidad, Activación y Protocolo", desc: "Las 6 fases del sistema. Flujo de caja en COD. Ruta según tu capital.", img: "/modulo-01.webp" },
    { num: "02", title: "Mercado", sub: "Arquitectura del Ecommerce COD en LATAM", desc: "Qué país elegir. Dropshipping vs Importación. Decisión según tu capital.", img: "/modulo-02.webp" },
    { num: "03", title: "Plataformas", sub: "Dropi, Effi y Plataformas COD LATAM", desc: "Cómo funcionan. Integración con Shopify. Tabla de decisión por país.", img: "/modulo-03.webp" },
    { num: "04", title: "Importaciones", sub: "De China a LATAM — El módulo más honesto", desc: "Alibaba, 1688. Negociación con proveedores. Los errores que costaron dinero real.", img: "/modulo-04.webp" },
    { num: "05", title: "Producto", sub: "Product Research Profesional COD", desc: "Ads Library, Gethookd, Minea. Calculadora de margen real incluida.", img: "/modulo-05.webp" },
    { num: "06", title: "Tienda", sub: "Tienda y Landing COD — Todo en pantalla", desc: "Shopify + Releasit completo. Blueprint de landing ganadora.", img: "/modulo-06.webp" },
    { num: "07", title: "Creativos", sub: "Creativos Profesionales para COD LATAM", desc: "Canva, CapCut, IA. Biblioteca de hooks por categoría de producto.", img: "/modulo-07.webp" },
    { num: "08", title: "Ads", sub: "Facebook y TikTok Ads — De 0 al Escalado", desc: "Testeo $20/día. Diagnóstico de métricas. Escalado horizontal y vertical.", img: "/modulo-08.webp" },
    { num: "09", title: "Finanzas", sub: "Control Financiero y Operativo", desc: "Flujo de caja real. Diferencia entre utilidad e ingreso bruto. CPA break-even.", img: "/modulo-09.webp" },
    { num: "10", title: "Logística", sub: "Logística, Confirmaciones y Operación COD", desc: "Flujo por país. Scripts de confirmación WhatsApp. Proceso de liquidaciones.", img: "/modulo-10.webp" },
    { num: "11", title: "Protección", sub: "Contingencias Meta y TikTok", desc: "Estructura de contingencia. Multilogin. Manejo de baneos de cuentas.", img: "/modulo-11.webp" },
    { num: "12", title: "Escalado", sub: "Gestión Empresarial y Escalado Real", desc: "Cuándo contratar. Dashboard con equipo. Multi-país. De vendedor a CEO.", img: "/modulo-12.webp" },
    { num: "13", title: "En Vivo", sub: "Módulo Técnico Operación en Vivo", desc: "Ve el sistema completo funcionando.", img: "/modulo-13.webp" },
];

const FIT_YES = [
    "Tienes al menos $300 USD disponibles",
    "Quieres empezar en los próximos 30 días",
    "Entiendes que hay que invertir para crecer",
    "Estás en RD o Latinoamérica",
    "Dispuesto a ejecutar, no solo ver",
];

const FIT_NO = [
    "Buscas dinero sin invertir tiempo ni capital",
    "Crees que el ecommerce es automático",
    "Quieres ver videos sin aplicar nada",
    "No puedes comprometerte 2-3 horas diarias",
];

function CoverLightbox({ m, onClose, onPrev, onNext, onSelect }) {
    const idxActual = MODULES.findIndex((x) => x.num === m.num);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onPrev();
            if (e.key === "ArrowRight") onNext();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, onPrev, onNext]);

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#0d0d0d]/95 p-4 backdrop-blur-md"
            onClick={onClose}
            data-testid="cover-lightbox"
        >
            <button
                type="button"
                onClick={onClose}
                className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-[#333] bg-[#111] text-[#999] transition-colors hover:border-[#ff5c00] hover:text-[#ff5c00]"
                aria-label="Cerrar visor"
                data-testid="cover-lightbox-close"
            >
                <X className="h-5 w-5" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onPrev();
                }}
                className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#333] bg-[#111]/90 text-[#999] transition-all hover:scale-105 hover:border-[#ff5c00] hover:text-[#ff5c00] sm:left-6"
                aria-label="Carátula anterior"
                data-testid="cover-lightbox-prev"
            >
                <ChevronLeft className="h-6 w-6" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                }}
                className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#333] bg-[#111]/90 text-[#999] transition-all hover:scale-105 hover:border-[#ff5c00] hover:text-[#ff5c00] sm:right-6"
                aria-label="Carátula siguiente"
                data-testid="cover-lightbox-next"
            >
                <ChevronRight className="h-6 w-6" />
            </button>
            <motion.img
                key={m.img}
                src={m.img}
                alt={`Carátula módulo ${m.num}: ${m.title}`}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.5}
                onDragEnd={(e, info) => {
                    if (info.offset.x < -50) onNext();
                    else if (info.offset.x > 50) onPrev();
                }}
                initial={{ scale: 0.92, opacity: 0.4 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 26 }}
                className="max-h-[80vh] max-w-[92vw] touch-pan-y select-none rounded-2xl border border-[#ff5c00]/40 object-contain shadow-[0_30px_120px_-20px_rgba(255,92,0,0.4)]"
                onClick={(e) => e.stopPropagation()}
            />
            <p className="font-display mt-5 text-center text-sm font-bold text-[#f5f5f5]">
                <span className="text-grad">{m.num}</span> · {m.title}{" "}
                <span className="font-normal text-[#888]">— {m.sub}</span>
            </p>
            <div className="mt-4 flex items-center gap-1.5" data-testid="cover-lightbox-dots">
                {MODULES.map((mod, i) => (
                    <button
                        key={mod.num}
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect(mod);
                        }}
                        aria-label={`Ir al módulo ${mod.num}`}
                        data-testid={`cover-dot-${mod.num}`}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                            i === idxActual
                                ? "w-6 bg-gradient-to-r from-[#ff5c00] to-[#ff7a30]"
                                : "w-1.5 bg-[#333] hover:bg-[#555]"
                        }`}
                    />
                ))}
            </div>
        </motion.div>,
        document.body,
    );
}

export default function Programa() {
    const [visor, setVisor] = useState(null);

    return (
        <>
            <section className="relative overflow-hidden px-5 py-20 sm:py-28" data-testid="instructor-section">
                <div className="pointer-events-none absolute -left-40 top-1/4 h-72 w-72 rounded-full bg-[#ff5c00]/[0.05] blur-[100px]" />
                <div className="relative mx-auto max-w-4xl">
                    <Reveal>
                        <div className="mb-4 flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#ff5c00]" />
                            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#888]">Quién te enseña</span>
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="rounded-3xl border border-[#222] bg-[#111111] p-6 sm:p-10">
                            <div className="flex flex-col gap-8 sm:flex-row sm:items-start">
                                <div className="flex shrink-0 flex-col items-center text-center sm:w-44">
                                    <span className="relative shrink-0 overflow-hidden rounded-2xl border border-[#ff5c00]/50 shadow-[0_0_40px_-8px_rgba(255,92,0,0.45)]">
                                        <img
                                            src="/alexis.jpg"
                                            alt="Alexis Castillo, fundador de BIZecomacademy"
                                            className="h-44 w-32 object-cover object-top"
                                            loading="lazy"
                                            data-testid="instructor-photo"
                                        />
                                    </span>
                                    <p className="font-display mt-4 text-lg font-extrabold" data-testid="instructor-name">
                                        Alexis Castillo.
                                    </p>
                                    <p className="mt-1 text-xs font-medium uppercase tracking-[0.14em] text-[#ff7a30]">
                                        Fundador · BIZecomacademy
                                    </p>
                                </div>
                                <div className="flex-1">
                                    <blockquote className="border-l-2 border-[#ff5c00] pl-5" data-testid="instructor-quote">
                                        <p className="serif-accent text-xl leading-snug text-[#f5f5f5] sm:text-2xl">
                                            “En la consultoría vemos tu situación y te digo si esto es para ti — sin rodeos.”
                                        </p>
                                    </blockquote>
                                    <p className="mt-6 text-sm leading-relaxed text-[#999] sm:text-base">
                                        Empezó desde cero en Higüey, República Dominicana. Sin contactos, sin capital, sin nadie en
                                        la familia con un negocio online. Probó productos que nadie compraba, quemó dinero en ads, y
                                        todo su entorno le decía que parara.
                                    </p>
                                    <p className="mt-4 text-sm leading-relaxed text-[#999] sm:text-base">
                                        En algún momento decidió dejar de ver a otros lograr lo que él quería — y empezar a ejecutar
                                        de verdad.
                                    </p>
                                    <p className="mt-4 text-sm leading-relaxed text-[#c9c9c9] sm:text-base">
                                        Hoy factura entre $40K y $60K mensuales y tiene alumnos en Colombia, Perú, Ecuador, México y
                                        República Dominicana generando sus primeros ingresos con este mismo sistema.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="relative overflow-hidden px-5 py-20 sm:py-28" data-testid="modules-section">
                <div className="relative mx-auto max-w-6xl">
                    <Reveal>
                        <div className="mb-4 flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#ff5c00]" />
                            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#888]">Lo que vas a aprender</span>
                        </div>
                        <h2 className="font-display text-3xl font-extrabold leading-[1.08] sm:text-5xl">
                            14 módulos. <em className="serif-accent text-grad">De cero a escalar.</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={120} className="mt-12">
                        <div className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                            {MODULES.map((m) => (
                                <div
                                    key={m.num}
                                    className="group flex gap-5 border-b border-[#1e1e1e] py-5 transition-colors duration-300 hover:border-[#ff5c00]/30"
                                    data-testid={`module-${m.num}`}
                                >
                                    <div className="flex shrink-0 flex-col gap-2">
                                        <span className="font-display text-grad text-xl font-extrabold">{m.num}</span>
                                        {m.img && (
                                            <button
                                                type="button"
                                                onClick={() => setVisor(m)}
                                                className="cursor-zoom-in"
                                                aria-label={`Ampliar carátula del módulo ${m.num}`}
                                                data-testid={`module-cover-${m.num}`}
                                            >
                                                <img
                                                    src={m.img}
                                                    alt={`Carátula módulo ${m.num}: ${m.title}`}
                                                    loading="lazy"
                                                    className="pointer-events-none aspect-video w-24 rounded-lg border border-[#222] object-cover transition-transform duration-500 group-hover:scale-[1.04] sm:w-28"
                                                />
                                            </button>
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-display text-sm font-bold sm:text-base">
                                            {m.title} <span className="font-normal text-[#777]">— {m.sub}</span>
                                        </p>
                                        <p className="mt-1.5 text-sm leading-relaxed text-[#999]">{m.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="relative overflow-hidden px-5 py-20 sm:py-28" data-testid="fit-section">
                <div className="relative mx-auto max-w-4xl">
                    <Reveal className="text-center">
                        <div className="mb-4 flex items-center justify-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#ff5c00]" />
                            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#888]">
                                Sé honesto contigo mismo
                            </span>
                        </div>
                        <h2 className="font-display text-3xl font-extrabold leading-[1.08] sm:text-5xl">
                            ¿Esto es <em className="serif-accent text-grad">para ti?</em>
                        </h2>
                    </Reveal>

                    <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Reveal delay={100}>
                            <div className="h-full rounded-2xl border border-[#22c55e]/25 bg-[#111111] p-4 sm:p-5" data-testid="fit-yes-card">
                                <img
                                    src="/fit-si.webp"
                                    alt="Sí es para ti"
                                    loading="lazy"
                                    className="aspect-video w-full rounded-xl border border-[#22c55e]/20 object-cover"
                                />
                                <div className="p-2 pt-5 sm:p-3 sm:pt-5">
                                <p className="font-display flex items-center gap-2 text-base font-bold text-[#22c55e]">
                                    <Check className="h-5 w-5" strokeWidth={3} /> Sí es para ti si...
                                </p>
                                <ul className="mt-6 space-y-4">
                                    {FIT_YES.map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-sm text-[#c9c9c9]">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#22c55e]" strokeWidth={3} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={200}>
                            <div className="h-full rounded-2xl border border-[#ef4444]/25 bg-[#111111] p-4 sm:p-5" data-testid="fit-no-card">
                                <img
                                    src="/fit-no.webp"
                                    alt="No es para ti"
                                    loading="lazy"
                                    className="aspect-video w-full rounded-xl border border-[#ef4444]/20 object-cover"
                                />
                                <div className="p-2 pt-5 sm:p-3 sm:pt-5">
                                <p className="font-display flex items-center gap-2 text-base font-bold text-[#ef4444]">
                                    <X className="h-5 w-5" strokeWidth={3} /> No es para ti si...
                                </p>
                                <ul className="mt-6 space-y-4">
                                    {FIT_NO.map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-sm text-[#c9c9c9]">
                                            <X className="mt-0.5 h-4 w-4 shrink-0 text-[#ef4444]" strokeWidth={3} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
            {visor && (
                <CoverLightbox
                    m={visor}
                    onClose={() => setVisor(null)}
                    onPrev={() =>
                        setVisor((v) => {
                            const i = MODULES.findIndex((m) => m.num === v.num);
                            return MODULES[(i - 1 + MODULES.length) % MODULES.length];
                        })
                    }
                    onNext={() =>
                        setVisor((v) => {
                            const i = MODULES.findIndex((m) => m.num === v.num);
                            return MODULES[(i + 1) % MODULES.length];
                        })
                    }
                    onSelect={setVisor}
                />
            )}
        </>
    );
}
